/**
 * Unauthorized Route
 *
 * Displays when a user is authenticated but doesn't have the required
 * permissions to access a specific route.
 */

import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Link } from "@/components/aria";
import { Button } from "@/components/base/buttons/button";
import { useAuth } from "@/contexts/AuthProvider";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth";

/**
 * Unauthorized route definition
 */
export const Route = createFileRoute("/unauthorized")({
	component: UnauthorizedPage,
});

/**
 * Unauthorized Page Component
 */
function UnauthorizedPage() {
	const auth = useAuth();
	const { t } = useAppTranslation();

	const params = useParams({ strict: false });

	const userInfoQuery = useQuery(authQueryOptions.userInfo());

	const defaultOrgId =
		userInfoQuery.data?.organizationUserInfo?.[0]?.organization?.id ??
		"unknown";

	const currentOrgId =
		(params as { orgId?: string }).orgId ?? defaultOrgId ?? "unknown";

	return (
		<div className="min-h-screen bg-background-primary flex items-center justify-center">
			<div className="max-w-md w-full text-center space-y-8 p-8">
				{/* Unauthorized Icon */}
				<div className="text-8xl">{t("unauthorized.icons.accessDenied")}</div>

				{/* Error Message */}
				<div className="space-y-4">
					<h1 className="text-3xl font-bold text-text-primary">
						{t("unauthorized.accessDenied")}
					</h1>
					<p className="text-text-secondary">
						{t("unauthorized.noPermissions")}
					</p>
				</div>

				{/* User Info */}
				{auth.user && (
					<div className="bg-background-secondary rounded-lg p-4 border border-border-primary">
						<div className="text-sm text-text-secondary mb-2">
							{t("unauthorized.signedInAs")}
						</div>
						<div className="flex items-center justify-center space-x-3">
							{auth.user.avatar && (
								<img
									src={auth.user.avatar}
									alt={auth.user.displayName}
									className="w-8 h-8 rounded-full border border-border-primary"
								/>
							)}
							<div className="text-left">
								<div className="font-medium text-text-primary">
									{auth.user.displayName}
								</div>
								<div className="text-xs text-text-secondary">
									{auth.user.email}
								</div>
							</div>
						</div>

						{/* User Roles */}
						<div className="mt-3 pt-3 border-t border-border-primary">
							<div className="text-xs text-text-secondary mb-1">
								{t("unauthorized.yourRoles")}
							</div>
							<div className="flex flex-wrap gap-1">
								{/* {auth.user.roles.map((role) => (
									<span
										key={role.id}
										className="px-2 py-1 text-xs bg-background-tertiary text-text-secondary rounded"
									>
										{role.name}
									</span>
								))} */}
							</div>
						</div>
					</div>
				)}

				{/* Action Buttons */}
				<div className="space-y-3">
					<Link
						to={`/web/${currentOrgId}/home`}
						variant="primary"
						className="block w-full py-2 px-4 bg-brand-600 text-white rounded-md hover:bg-brand-700 transition-colors text-center"
					>
						{t("unauthorized.goToDashboard")}
					</Link>

					<Link
						to={`/web/${currentOrgId}/home`}
						variant="secondary"
						className="block w-full py-2 px-4 bg-background-secondary text-text-primary border border-border-primary rounded-md hover:bg-background-tertiary transition-colors text-center"
					>
						{t("unauthorized.goHome")}
					</Link>

					<Button
						onClick={() => auth.logout()}
						color="secondary"
						className="w-full"
					>
						{t("unauthorized.signOut")}
					</Button>
				</div>

				{/* Help Text */}
				<div className="text-xs text-text-tertiary bg-background-tertiary p-3 rounded">
					{t("unauthorized.ifYouBelieveYouShouldHaveAccessToThisResource")}
					{t("unauthorized.pleaseContactYourAdministrator")}
				</div>
			</div>
		</div>
	);
}
